import React, { useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoadingSpinner from 'components/common/LoadingSpinner';
import useAccountRecovery from '../../../hooks/auth/useAccountRecovery';
import paths from '../../../routes/paths';

// Helper style function for inputs
const getInputStyles = () => ({
  backgroundColor: '#fff',
  borderColor: '#b0b0b0',
  fontSize: '1rem',
  fontWeight: '400',
  transition: 'border-color 0.2s ease'
});

const PasswordReset = () => {
  const { resetPassword, isLoading } = useAccountRecovery();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validation function
  const validateForm = () => {
    const errors = {};

    if (!formData.newPassword.trim()) {
      errors.newPassword = 'Please enter a new password';
    } else if (formData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submit handler
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (validateForm()) {
      await resetPassword(formData.newPassword, formData.confirmPassword);
    }
  };

  // Field change with validation
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear errors when user types
    if (showValidation && validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Real-time validation for password match
    if (name === 'confirmPassword' && formData.newPassword) {
      if (value !== formData.newPassword) {
        setValidationErrors(prev => ({
          ...prev,
          confirmPassword: 'Passwords do not match'
        }));
      } else {
        setValidationErrors(prev => ({
          ...prev,
          confirmPassword: ''
        }));
      }
    }
  };

  // Input style handlers
  const handleFocus = (e) => { e.target.style.borderColor = '#000'; };
  const handleBlur = (e) => { e.target.style.borderColor = '#b0b0b0'; };
  const handleMouseEnter = (e) => { e.target.style.borderColor = '#000'; };
  const handleMouseLeave = (e) => { e.target.style.borderColor = '#b0b0b0'; };

  return (
    <>
      <div className="text-center mb-4">
        <h5 className="mb-2">Reset your password</h5>
        <p className="text-muted mb-0 fs-10">
          Please enter your new password below. Make sure it's at least 6 characters long.
        </p>
      </div>

      <Form onSubmit={handleFormSubmit}>
        {/* New Password */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-normal text-dark">New Password</Form.Label>
          <div className="position-relative">
            <Form.Control
              placeholder="Enter new password"
              value={formData.newPassword}
              name="newPassword"
              onChange={handleFieldChange}
              type={showPassword ? 'text' : 'password'}
              isInvalid={showValidation && !!validationErrors.newPassword}
              className="form-control-lg border-1"
              style={{ ...getInputStyles(), paddingRight: '45px' }}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              disabled={isLoading}
            />
            <button
              type="button"
              className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                cursor: 'pointer',
                right: '15px',
                zIndex: '10',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <FontAwesomeIcon
                icon={showPassword ? 'eye-slash' : 'eye'}
                className="text-muted"
                style={{ fontSize: '16px', color: '#6c757d', transition: 'color 0.2s ease' }}
              />
            </button>
            {showValidation && validationErrors.newPassword && (
              <Form.Control.Feedback type="invalid" style={{ position: 'relative', zIndex: '1', display: 'block' }}>
                {validationErrors.newPassword}
              </Form.Control.Feedback>
            )}
          </div>
        </Form.Group>

        {/* Confirm Password */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-normal text-dark">Confirm Password</Form.Label>
          <div className="position-relative">
            <Form.Control
              placeholder="Confirm new password"
              value={formData.confirmPassword}
              name="confirmPassword"
              onChange={handleFieldChange}
              type={showConfirmPassword ? 'text' : 'password'}
              isInvalid={showValidation && !!validationErrors.confirmPassword}
              className="form-control-lg border-1"
              style={{ ...getInputStyles(), paddingRight: '45px' }}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              disabled={isLoading}
            />
            <button
              type="button"
              className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                cursor: 'pointer',
                right: '15px',
                zIndex: '10',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <FontAwesomeIcon
                icon={showConfirmPassword ? 'eye-slash' : 'eye'}
                className="text-muted"
                style={{ fontSize: '16px', color: '#6c757d', transition: 'color 0.2s ease' }}
              />
            </button>
            {showValidation && validationErrors.confirmPassword && (
              <Form.Control.Feedback type="invalid" style={{ position: 'relative', zIndex: '1', display: 'block' }}>
                {validationErrors.confirmPassword}
              </Form.Control.Feedback>
            )}
          </div>
        </Form.Group>

        {/* Submit Button */}
        <Form.Group className="mb-3">
          <Button
            type="submit"
            variant="success"
            className="w-100 btn-lg fw-semibold d-flex align-items-center justify-content-center"
            style={{
              fontSize: '1.1rem',
              padding: '0.5rem 1.2rem',
              fontWeight: '600',
              textAlign: 'center'
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <LoadingSpinner size="sm" text="Resetting..." showText={false} />
            ) : (
              'Reset Password'
            )}
          </Button>
          {showValidation && (validationErrors.newPassword || validationErrors.confirmPassword) && (
            <Alert variant="warning" className="mt-2 mb-0">
              <small>Please fix the errors above to continue.</small>
            </Alert>
          )}
        </Form.Group>

        {/* Back to Login */}
        <div className="text-center">
          <Link 
            to={paths.simpleLogin} 
            className="fs-10 text-decoration-none"
            style={{ color: '#6c757d' }}
          >
            <FontAwesomeIcon icon="chevron-left" className="me-1" />
            Back to login
          </Link>
        </div>
      </Form>
    </>
  );
};

export default PasswordReset;
